import { brandPalette } from "../../../constants/brandPalette";
import {
  Avatar,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { MdAccessTime, MdAssignment, MdInfoOutline, MdPalette } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { getExamsByIdRequest } from "../../../modules/students-modules/exams/Actions";
import { studentInk, studentPalette } from "../../../constants/studentPalette";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import { isStudentOwnedExamFlow } from "../../../utils/studentExamPaths";

const FONT_STACK = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;

const C = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
  pageBg: studentSurfaces.canvas,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  ink: studentInk,
  muted: studentSurfaces.textMuted,
};

const gridPattern = {
  bg: C.pageBg,
  backgroundImage: `radial-gradient(${brandPalette.stone} 1px, transparent 1px)`,
  backgroundSize: "32px 32px",
};

const cardShell = {
  bg: studentSurfaces.raised,
  borderWidth: "1px",
  borderColor: C.border,
  borderRadius: "2xl",
  boxShadow: "0 8px 28px rgba(91, 13, 22, 0.07)",
  position: "relative",
  overflow: "hidden",
};

const accentBar = {
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  w: "6px",
  bg: C.primary,
  borderRadius: "full",
};

const ExameGuide = () => {
  const dispatch = useDispatch();
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const pathname = window.location.pathname;
  const isStudentFlow = isStudentOwnedExamFlow(pathname);
  const examId = isStudentFlow
    ? pathname.split("/")[3]
    : pathname.split("/")[2];

  const exam = useSelector((state) => state.chapterExams.exam);
  const isLoading = useSelector((state) => state.chapterExams.isLoading);
  const userData = useSelector((state) => state.user.userData);

  useEffect(() => {
    if (examId) dispatch(getExamsByIdRequest(examId));
  }, [examId, dispatch]);

  useEffect(() => {
    const q = queryString.parse(window.location.search);
    if (q.returnTo) {
      try {
        sessionStorage.setItem("edutopia_post_exam_redirect", q.returnTo);
      } catch {
        /* ignore */
      }
    }
  }, [examId]);

  const title = parsed?.name || exam?.name || "Exam";
  const timed = exam?.timer == 1;
  const questionCount = exam?.total_count;

  const startSolve = () => {
    try {
      localStorage.setItem("startTime", new Date().toISOString());
    } catch {
      /* ignore */
    }
    const q = new URLSearchParams({ name: title });
    if (parsed?.returnTo) q.set("returnTo", parsed.returnTo);
    const qs = q.toString();
    isStudentFlow
      ? window.location.replace(`/student/solve-exam/${examId}?${qs}`)
      : window.location.replace(`/solve-exam/${examId}?${qs}`);
  };

  if (!examId || isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" {...gridPattern} fontFamily={FONT_STACK}>
        <Spinner size="xl" thickness="4px" color={C.primary} />
      </Flex>
    );
  }

  return (
    <Box minH="100vh" fontFamily={FONT_STACK} color="var(--text-primary)" {...gridPattern}>
      <Flex
        as="header"
        align="center"
        justify="space-between"
        gap={4}
        flexWrap="wrap"
        px={{ base: 4, md: 8 }}
        py={4}
        bg={C.pageBg}
        borderBottomWidth="1px"
        borderColor={C.border}
      >
        <Heading
          as="h1"
          fontSize={{ base: "md", md: "xl" }}
          fontWeight="800"
          color={C.primary}
          textTransform="uppercase"
          letterSpacing="0.06em"
          lineHeight="1.25"
          maxW={{ base: "100%", md: "70%" }}
        >
          {title}
        </Heading>
        <HStack spacing={3} flexShrink={0}>
          {timed ? (
            <HStack
              spacing={2}
              px={4}
              py={2}
              borderWidth="1px"
              borderColor={C.border}
              bg={C.cream}
              borderRadius="xl"
            >
              <MdAccessTime color={C.primary} size={22} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.1em">
                  Time limit
                </Text>
                <Text fontWeight="800" color={C.primary} fontSize="md">
                  {exam?.time} min
                </Text>
              </VStack>
            </HStack>
          ) : null}
          <Avatar
            size="sm"
            name={userData?.name || userData?.username || "Student"}
            src={userData?.image || userData?.avatar}
            borderRadius="md"
            borderWidth="1px"
            borderColor={C.border}
          />
        </HStack>
      </Flex>

      <Box maxW="3xl" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 8, md: 12 }} pb={24}>
        <Text
          fontSize="sm"
          color={C.muted}
          fontWeight="600"
          fontStyle="italic"
          mb={8}
          lineHeight="tall"
        >
          When you begin the exam, the timer will count down from your allotted time. Good luck.
        </Text>

        <VStack spacing={6} align="stretch">
          <Box {...cardShell} pl={{ base: 5, md: 8 }} pr={6} py={{ base: 6, md: 8 }}>
            <Box {...accentBar} />
            <HStack spacing={3} mb={4} pl={2}>
              <Box color={C.primary}>
                <MdInfoOutline size={24} />
              </Box>
              <Text fontSize="xs" fontWeight="800" color={C.primary} textTransform="uppercase" letterSpacing="0.14em">
                Instructions
              </Text>
            </HStack>
            <UnorderedList spacing={3} pl={{ base: 2, md: 4 }} color="var(--text-primary)" fontWeight="500" lineHeight="tall">
              <ListItem>Do not reload or refresh the exam page; your progress may be lost.</ListItem>
              <ListItem>Answer every question before submitting. You can move between questions freely.</ListItem>
              <ListItem>
                If the timer runs out before you submit, your answers may be saved automatically and the session may
                end.
              </ListItem>
            </UnorderedList>
            {questionCount != null ? (
              <HStack mt={6} spacing={2} color={C.muted} pl={2}>
                <MdAssignment size={20} />
                <Text fontSize="sm" fontWeight="700">
                  {questionCount} question{questionCount === 1 ? "" : "s"} in this exam
                </Text>
              </HStack>
            ) : null}
          </Box>

          <Box {...cardShell} pl={{ base: 5, md: 8 }} pr={6} py={{ base: 6, md: 8 }}>
            <Box {...accentBar} />
            <HStack spacing={3} mb={4} pl={2}>
              <Box color={C.primary}>
                <MdPalette size={24} />
              </Box>
              <Text fontSize="xs" fontWeight="800" color={C.primary} textTransform="uppercase" letterSpacing="0.14em">
                Question navigator
              </Text>
            </HStack>
            <Text mb={4} pl={2} color="var(--text-primary)" lineHeight="tall" fontWeight="500">
              During the exam, the numbers at the bottom show your progress:
            </Text>
            <UnorderedList spacing={3} pl={{ base: 2, md: 4 }} color="var(--text-primary)" fontWeight="500" lineHeight="tall">
              <ListItem>
                <Text as="span" fontWeight="800" color={C.primary}>
                  Filled (maroon)
                </Text>{" "}
                — you have selected an answer for that question.
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="800" color={C.primary}>
                  Bold outline
                </Text>{" "}
                — the question you are viewing now.
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="800" color={C.muted}>
                  Light border
                </Text>{" "}
                — not yet answered.
              </ListItem>
            </UnorderedList>
          </Box>

          <Button
            w="full"
            maxW="md"
            mx="auto"
            bg={C.primary}
            color="white"
            size="lg"
            py={8}
            borderRadius="2xl"
            fontWeight="800"
            textTransform="uppercase"
            letterSpacing="0.12em"
            fontSize="sm"
            _hover={{ bg: C.accent }}
            boxShadow="0 12px 32px rgba(91, 13, 22, 0.25)"
            onClick={startSolve}
          >
            Start your exam now
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default ExameGuide;
